/**
 * Rendu de la chute d'eau: le ruisseau, la lame, l'ecume et les embruns.
 *
 * POURQUOI UN FICHIER A PART. La nappe de `eau.ts` est UNE surface posee au
 * niveau de la mer: elle remplit toute seule la mer, les lacs, les rivieres et
 * la vasque de la chute, mais elle ne peut par construction representer ni une
 * lame d'eau VERTICALE, ni un ruisseau qui coule douze metres au-dessus d'elle.
 * Il faut donc une geometrie dediee, et elle a ses propres regles: elle suit un
 * axe et non un plan, elle est animee vers le BAS et non en houle, et elle est
 * blanche et non turquoise.
 *
 * TROIS MAILLAGES, TROIS APPELS DE DESSIN:
 *
 *  - `lame`, le RIDEAU: trois voiles superposes qui tombent de la crete
 *    jusqu'au plan d'eau. Leur trajet est une parabole — l'eau part avec la
 *    vitesse du courant et prend le vide — mais chaque point est repousse
 *    devant le rocher qu'il croise, si bien que le rideau epouse le cirque au
 *    lieu de le traverser. Les trois voiles descendent a des vitesses
 *    differentes: c'est ce qui donne de l'epaisseur a une surface plate.
 *
 *  - `nappes`, LES SURFACES HORIZONTALES: le miroir du ruisseau en amont, le
 *    bourrelet de la crete, et l'ecume circulaire du bassin de reception. Elles
 *    partagent un seul nuanceur parce qu'elles posent toutes la meme question —
 *    « de l'eau qui court dans une direction » — et ne different que par leurs
 *    attributs.
 *
 *  - `embruns`, LA POUSSIERE D'EAU: des facettes tournees vers la camera qui
 *    montent du pied de la chute et s'evanouissent. Elles sont animees
 *    entierement dans le nuanceur de sommets a partir d'une phase par facette:
 *    aucune mise a jour de tampon, aucun cout par image cote processeur.
 *
 * CE QUI N'EST PAS FAIT ICI, ET C'EST VOULU: aucune lumiere n'est ajoutee. Le
 * nombre de lumieres d'une scene fait partie de la cle de cache des programmes
 * de three.js, en ajouter une recompilerait tous les nuanceurs du jeu. Les
 * materiaux sont des `MeshLambertMaterial` eclaires par la meme source que le
 * reste du monde: la chute devient donc ambre au couchant et bleutee la nuit
 * sans qu'on ait rien a piloter, et il ne reste a regler que la part d'emission
 * propre de l'eau blanche, qui garde le rideau lisible sous la lune.
 */
import * as THREE from 'three'
import type { Chute } from '../../shared/sim/terrain.ts'
import type { Ile } from '../../shared/sim/carte.ts'
import { clamp, lerp, smoothstep } from '../../shared/sim/alea.ts'
import { journal } from '../log/journal.ts'

/** Colonnes et niveaux du rideau. 15 x 30 suffit a une lame de 12 m. */
const COLONNES = 15
const NIVEAUX = 30

/**
 * Decalage vers l'aval, largeur, opacite et vitesse de chacun des trois voiles.
 *
 * Les opacites sont volontairement basses: elles S'ADDITIONNENT la ou les trois
 * se superposent, et un rideau opaque n'est plus de l'eau, c'est un drap. Ce
 * qu'on cherche, c'est de voir le rocher au travers du haut de la lame et de ne
 * plus le voir du tout en bas, la ou l'eau s'est chargee d'air.
 */
const VOILES: readonly { avance: number; largeur: number; opacite: number; vitesse: number }[] = [
  { avance: 0, largeur: 0.98, opacite: 0.62, vitesse: 0.62 },
  { avance: 0.28, largeur: 1.06, opacite: 0.4, vitesse: 0.85 },
  { avance: 0.58, largeur: 1.14, opacite: 0.22, vitesse: 1.12 },
]

/** Vitesse du courant a la bascule, en m/s: elle donne l'avancee de la parabole. */
const VITESSE_CRETE = 2.4
const PESANTEUR = 9.81

/** Marge devant le rocher, en metres: le rideau ne doit jamais le traverser. */
const MARGE_ROCHER = 0.35

/** Elargissement de la lame entre la crete et le plan d'eau. */
const EVASEMENT = 0.5

/** Profondeur d'eau du ruisseau, en metres. */
const CREUX_RUISSEAU = 0.34

/** Demi-largeur du miroir du ruisseau, en fraction de la demi-largeur du lit. */
const PART_MIROIR = 0.8

/** Rayon, secteurs et anneaux de l'ecume du bassin. */
const RAYON_BASSIN = 5.6
const SECTEURS_BASSIN = 32
const ANNEAUX_BASSIN = 7

/** Opacite de l'ecume au point de chute. Elle tombe a zero au bord du bassin. */
const ALPHA_BASSIN = 0.52

/** Nombre de facettes d'embruns, et hauteur a laquelle elles s'eteignent. */
const EMBRUNS = 46
const MONTEE_EMBRUNS = 5

/**
 * Teintes: l'eau vive des hauts-fonds du ruisseau, celle de son fil, et le
 * blanc casse de l'ecume.
 *
 * Le ruisseau porte un DEGRADE EN TRAVERS, sombre au milieu et clair sur les
 * bords. Sans lui, la bande d'un seul cyan pale posee au fond de sa gorge ne se
 * lisait pas comme de l'eau mais comme un sentier de dalles: c'est la profondeur
 * qui fait reconnaitre un cours d'eau, meme large de quatre metres.
 */
const EAU_VIVE = 0x63c1d6
const EAU_FIL = 0x2f92b2
const BLANC_ECUME = 0xeffbff

/** Hauteur du miroir du bassin au-dessus du plan d'eau, en metres. */
const POSE_ECUME = 0.07

/**
 * Emission propre de l'eau blanche, de jour et de nuit.
 *
 * Une lame verticale ne recoit presque rien d'un soleil qui vient d'en haut:
 * eclairee au seul Lambert, la chute etait grise en plein midi. Une part
 * d'emission la remet a sa vraie valeur — une chute d'eau est l'objet le plus
 * clair d'un paysage — sans ajouter la moindre lumiere a la scene. La nuit on
 * n'eteint pas tout: une chute reste visible sous la lune, et c'est aussi ce
 * qui evite qu'elle disparaisse du paysage entre 20 h 30 et 6 h.
 */
const EMISSION_JOUR = 0.26
const EMISSION_NUIT = 0.2

/**
 * Part de cette emission que recoivent les surfaces horizontales.
 *
 * Elles n'en ont presque pas besoin: elles regardent le ciel et le Lambert les
 * sert deja bien. Leur en donner autant qu'a la lame delavait le ruisseau en un
 * ruban de beton clair au milieu de la foret.
 */
const PART_EMISSION_NAPPES = 0.28

/** Altitude d'un point du lit, `remont` metres en amont de la crete. */
function altitudeLit(c: Chute, remont: number): number {
  if (remont <= 0) return c.hCrete
  const i = Math.floor(remont)
  if (i >= c.longAmont) return c.lit[c.longAmont] ?? c.hCrete
  const a = c.lit[i] ?? c.hCrete
  const b = c.lit[i + 1] ?? a
  return a + (b - a) * (remont - i)
}

export class RenduChute {
  private scene: THREE.Scene
  private chute: Chute | null
  private uTemps = { value: 0 }
  private uNuit = { value: 0 }
  private objets: THREE.Mesh[] = []
  private materiaux: THREE.Material[] = []
  private matLame: THREE.MeshLambertMaterial | null = null
  private matNappes: THREE.MeshLambertMaterial | null = null

  constructor(scene: THREE.Scene, terrain: Ile) {
    this.scene = scene
    this.chute = terrain.chute
    const c = this.chute
    if (!c) return
    const t0 = performance.now()

    const tris =
      this.construireLame(c, terrain) + this.construireNappes(c, terrain) + this.construireEmbruns(c, terrain)
    journal.info(
      'chute',
      `chute d'eau en ${c.x.toFixed(0)}, ${c.z.toFixed(0)} · ${(c.hCrete - c.hBassin).toFixed(1)} m de haut · ${tris} triangles`,
      { ms: Math.round(performance.now() - t0) },
    )
  }

  // ------------------------------------------------------------------ reperes

  /** Point du monde a `s` metres en aval de la crete et `n` metres a droite. */
  private point(c: Chute, s: number, n: number): [number, number] {
    return [c.x + c.dx * s + c.dz * n, c.z + c.dz * s - c.dx * n]
  }

  /**
   * Abscisse a laquelle le rocher passe sous l'altitude `y`, sur la ligne
   * laterale `n`. C'est ce qui plaque le rideau contre le cirque: la falaise
   * est un fer a cheval, sa levre n'est pas a la meme distance au milieu et sur
   * les bords, et une lame plate aurait traverse la roche d'un cote et flotte
   * dans le vide de l'autre.
   */
  /**
   * Ou la lame touche le plan d'eau. Elle ne tombe pas au pied du rocher mais
   * un peu plus loin, poussee par la vitesse du courant: l'ecume et les embruns
   * doivent viser le meme endroit qu'elle, sinon la chute frappe a cote.
   */
  private pointDeChute(c: Chute, terrain: Ile): [number, number] {
    const rocher = this.bordRocher(c, terrain, 0, c.hBassin + 0.5)
    const parabole = VITESSE_CRETE * Math.sqrt((2 * Math.max(0, c.hCrete - c.hBassin)) / PESANTEUR)
    return this.point(c, Math.max(rocher + 1.2, parabole), 0)
  }

  private bordRocher(c: Chute, terrain: Ile, n: number, y: number): number {
    for (let s = -2; s <= 14; s += 0.25) {
      const p = this.point(c, s, n)
      if (terrain.hauteur(p[0], p[1]) <= y) return s
    }
    return 14
  }

  // -------------------------------------------------------------------- lame

  private construireLame(c: Chute, terrain: Ile): number {
    const nbVoiles = VOILES.length
    const parVoile = COLONNES * NIVEAUX
    const total = parVoile * nbVoiles
    const positions = new Float32Array(total * 3)
    const normales = new Float32Array(total * 3)
    const couleurs = new Float32Array(total * 4)
    const uv = new Float32Array(total * 2)
    const phases = new Float32Array(total)
    const vitesses = new Float32Array(total)
    const index: number[] = []

    const teinteHaut = new THREE.Color(EAU_VIVE)
    const teinteBas = new THREE.Color(BLANC_ECUME)
    const melange = new THREE.Color()

    for (let v = 0; v < nbVoiles; v++) {
      const voile = VOILES[v] ?? VOILES[0]
      if (!voile) continue
      const base = v * parVoile
      // La lame ne recule jamais: on garde l'abscisse la plus avancee vue plus
      // haut sur la meme colonne, sinon un surplomb de rocher ferait rentrer le
      // rideau dans la falaise deux metres plus bas.
      const avancee = new Float32Array(COLONNES)
      for (let i = 0; i < NIVEAUX; i++) {
        const part = i / (NIVEAUX - 1)
        const y = lerp(c.hCrete, c.hBassin, part)
        const chute = c.hCrete - y
        // Parabole: l'eau garde sa vitesse horizontale et prend le vide.
        const temps = Math.sqrt((2 * Math.max(0, chute)) / PESANTEUR)
        const parabole = VITESSE_CRETE * temps + voile.avance
        const demi = c.demiLit * voile.largeur * (1 + EVASEMENT * part)
        for (let j = 0; j < COLONNES; j++) {
          const u = COLONNES > 1 ? (j / (COLONNES - 1)) * 2 - 1 : 0
          const n = u * demi
          let s = Math.max(parabole, this.bordRocher(c, terrain, n, y) + MARGE_ROCHER)
          const vu = avancee[j] ?? 0
          if (s < vu) s = vu
          avancee[j] = s
          const p = this.point(c, s, n)
          const k = base + i * COLONNES + j
          positions[k * 3] = p[0]
          positions[k * 3 + 1] = y
          positions[k * 3 + 2] = p[1]
          // La normale regarde l'aval, redressee vers le haut: une surface
          // strictement verticale ne recoit rien d'un soleil zenithal.
          normales[k * 3] = c.dx * 0.62
          normales[k * 3 + 1] = 0.78
          normales[k * 3 + 2] = c.dz * 0.62
          melange.copy(teinteHaut).lerp(teinteBas, smoothstep(0.05, 0.75, part))
          couleurs[k * 4] = melange.r
          couleurs[k * 4 + 1] = melange.g
          couleurs[k * 4 + 2] = melange.b
          couleurs[k * 4 + 3] = voile.opacite
          uv[k * 2] = u
          uv[k * 2 + 1] = part
          // Phase par colonne: les filets d'eau ne tombent pas en cadence.
          phases[k] = v * 2.1 + j * 0.77 + Math.sin(j * 12.9898 + v * 4.1) * 1.7
          vitesses[k] = voile.vitesse
        }
      }
      for (let i = 0; i < NIVEAUX - 1; i++) {
        for (let j = 0; j < COLONNES - 1; j++) {
          const a = base + i * COLONNES + j
          index.push(a, a + COLONNES, a + 1, a + 1, a + COLONNES, a + COLONNES + 1)
        }
      }
    }

    const geo = new THREE.BufferGeometry()
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    geo.setAttribute('normal', new THREE.BufferAttribute(normales, 3))
    geo.setAttribute('color', new THREE.BufferAttribute(couleurs, 4))
    geo.setAttribute('aLame', new THREE.BufferAttribute(uv, 2))
    geo.setAttribute('aPhase', new THREE.BufferAttribute(phases, 1))
    geo.setAttribute('aVitesse', new THREE.BufferAttribute(vitesses, 1))
    geo.setIndex(index)
    geo.computeBoundingSphere()

    this.matLame = new THREE.MeshLambertMaterial({
      color: 0xffffff,
      vertexColors: true,
      transparent: true,
      opacity: 1,
      depthWrite: false,
      side: THREE.DoubleSide,
      emissive: new THREE.Color(BLANC_ECUME),
      emissiveIntensity: EMISSION_JOUR,
    })
    this.installerNuanceurLame(this.matLame)
    this.ajouter(new THREE.Mesh(geo, this.matLame), 'chute-lame', 4)
    return index.length / 3
  }

  // ------------------------------------------------------------------ nappes

  /**
   * Les trois surfaces horizontales, dans un seul maillage.
   *
   * Chaque sommet porte sa couleur, sa distance parcourue le long du courant
   * (`aCourant`) et la vitesse a laquelle le motif doit y defiler: le nuanceur
   * n'a plus besoin de savoir a quelle piece il appartient.
   */
  private construireNappes(c: Chute, terrain: Ile): number {
    const positions: number[] = []
    const couleurs: number[] = []
    const courants: number[] = []
    const index: number[] = []
    const teinte = new THREE.Color()

    const poser = (
      x: number,
      y: number,
      z: number,
      couleur: number,
      alpha: number,
      along: number,
      vitesse: number,
      travers: number,
    ): number => {
      const k = positions.length / 3
      positions.push(x, y, z)
      teinte.setHex(couleur)
      couleurs.push(teinte.r, teinte.g, teinte.b, alpha)
      courants.push(along, vitesse, travers)
      return k
    }
    const quad = (a: number, b: number, cc: number, d: number): void => {
      index.push(a, b, cc, cc, b, d)
    }

    // --- le ruisseau. Quatre rangs: les deux bords tombent au fond du lit avec
    // une opacite nulle, si bien que le miroir n'a AUCUNE arete visible la ou
    // la berge est plus basse que le fil de l'eau.
    // Ecart au fil de l'eau, opacite, enfoncement, et part de la teinte du fil.
    const rangs: readonly [number, number, number, number][] = [
      [-1, 0, -CREUX_RUISSEAU, 0],
      [-0.72, 1, 0, 0],
      [0, 1, 0, 1],
      [0.72, 1, 0, 0],
      [1, 0, -CREUX_RUISSEAU, 0],
    ]
    const pas = 1
    let precedent: number[] | null = null
    for (let remont = c.longAmont; remont >= -0.6; remont -= pas) {
      const lit = altitudeLit(c, Math.max(0, remont))
      const y = lit + CREUX_RUISSEAU
      const demi = c.demiLit * PART_MIROIR
      const ligne: number[] = []
      for (const rang of rangs) {
        const n = (rang[0] ?? 0) * demi
        const p = this.point(c, -remont, n)
        // Le miroir s'efface a l'amont: le ruisseau nait du sous-bois.
        const vif = smoothstep(c.longAmont, c.longAmont - 8, remont)
        const couleur = (rang[3] ?? 0) > 0.5 ? EAU_FIL : EAU_VIVE
        ligne.push(poser(p[0], y + (rang[2] ?? 0), p[1], couleur, (rang[1] ?? 0) * 0.76 * vif, remont, 1, n))
      }
      if (precedent) {
        for (let r = 0; r < rangs.length - 1; r++) {
          quad(precedent[r] ?? 0, precedent[r + 1] ?? 0, ligne[r] ?? 0, ligne[r + 1] ?? 0)
        }
      }
      precedent = ligne
    }

    // --- le bourrelet de la crete: l'eau blanchit juste avant de basculer.
    const demiCrete = c.demiLit * PART_MIROIR
    const crete: number[][] = []
    for (let i = 0; i <= 1; i++) {
      const s = i === 0 ? -2.2 : 0.5
      const ligne: number[] = []
      for (let j = 0; j <= 6; j++) {
        const u = (j / 6) * 2 - 1
        const n = u * demiCrete * (1 + 0.1 * i)
        const p = this.point(c, s, n)
        const bord = 1 - smoothstep(0.72, 1, Math.abs(u))
        ligne.push(poser(p[0], c.hCrete + CREUX_RUISSEAU + 0.02, p[1], BLANC_ECUME, bord * (i === 0 ? 0.08 : 0.5), -s, 2.4, n))
      }
      crete.push(ligne)
    }
    const c0 = crete[0]
    const c1 = crete[1]
    if (c0 && c1) {
      for (let j = 0; j < 6; j++) quad(c0[j] ?? 0, c0[j + 1] ?? 0, c1[j] ?? 0, c1[j + 1] ?? 0)
    }

    // --- l'ecume du bassin: des anneaux qui s'ecartent du point de chute.
    const impact = this.pointDeChute(c, terrain)
    const anneaux: number[][] = []
    for (let a = 0; a <= ANNEAUX_BASSIN; a++) {
      const rayon = (a / ANNEAUX_BASSIN) * RAYON_BASSIN
      const ligne: number[] = []
      for (let j = 0; j <= SECTEURS_BASSIN; j++) {
        const angle = (j / SECTEURS_BASSIN) * Math.PI * 2
        const x = impact[0] + Math.cos(angle) * rayon
        const z = impact[1] + Math.sin(angle) * rayon
        // Le bassin est plus long que large: l'eau part vers l'aval.
        const etire = 1 + 0.35 * (Math.cos(angle) * c.dx + Math.sin(angle) * c.dz)
        const xx = impact[0] + (x - impact[0]) * etire
        const zz = impact[1] + (z - impact[1]) * etire
        // L'ecume s'eteint en puissance et non en marche: sept anneaux ne
        // suffisent pas a cacher un degrade brutal, et la premiere version
        // posait sur la vasque un disque blanc a bord net.
        const part = a / ANNEAUX_BASSIN
        const alpha = ALPHA_BASSIN * Math.pow(1 - part, 1.7)
        ligne.push(poser(xx, c.hBassin + POSE_ECUME, zz, BLANC_ECUME, alpha, rayon, 1.5, angle * 3))
      }
      anneaux.push(ligne)
    }
    for (let a = 0; a < ANNEAUX_BASSIN; a++) {
      const bas = anneaux[a]
      const haut = anneaux[a + 1]
      if (!bas || !haut) continue
      for (let j = 0; j < SECTEURS_BASSIN; j++) quad(bas[j] ?? 0, bas[j + 1] ?? 0, haut[j] ?? 0, haut[j + 1] ?? 0)
    }

    const nb = positions.length / 3
    const normales = new Float32Array(nb * 3)
    for (let k = 0; k < nb; k++) normales[k * 3 + 1] = 1

    const geo = new THREE.BufferGeometry()
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(positions), 3))
    geo.setAttribute('normal', new THREE.BufferAttribute(normales, 3))
    geo.setAttribute('color', new THREE.BufferAttribute(new Float32Array(couleurs), 4))
    geo.setAttribute('aCourant', new THREE.BufferAttribute(new Float32Array(courants), 3))
    geo.setIndex(index)
    geo.computeBoundingSphere()

    this.matNappes = new THREE.MeshLambertMaterial({
      color: 0xffffff,
      vertexColors: true,
      transparent: true,
      opacity: 1,
      depthWrite: false,
      side: THREE.DoubleSide,
      emissive: new THREE.Color(BLANC_ECUME),
      emissiveIntensity: EMISSION_JOUR * PART_EMISSION_NAPPES,
    })
    this.installerNuanceurNappes(this.matNappes)
    this.ajouter(new THREE.Mesh(geo, this.matNappes), 'chute-nappes', 4)
    return index.length / 3
  }

  // ----------------------------------------------------------------- embruns

  /**
   * Les embruns: une facette par gouttelette, tournee vers la camera dans le
   * nuanceur de sommets.
   *
   * Tout le mouvement — montee, dilatation, disparition — se deduit d'une phase
   * ecrite une fois pour toutes dans la geometrie: il n'y a rien a mettre a
   * jour par image, et la nuee tient dans un seul appel de dessin.
   */
  private construireEmbruns(c: Chute, terrain: Ile): number {
    const impact = this.pointDeChute(c, terrain)
    const centres = new Float32Array(EMBRUNS * 4 * 3)
    const coins = new Float32Array(EMBRUNS * 4 * 2)
    const vies = new Float32Array(EMBRUNS * 4 * 3)
    const positions = new Float32Array(EMBRUNS * 4 * 3)
    const normales = new Float32Array(EMBRUNS * 4 * 3)
    const index: number[] = []

    for (let e = 0; e < EMBRUNS; e++) {
      // Semis regulier tourne par le nombre d'or: pas de motif visible, et
      // aucun tirage aleatoire — la chute doit etre la meme partout.
      const t = (e + 0.5) / EMBRUNS
      const angle = e * 2.399963
      const rayon = Math.sqrt(t) * RAYON_BASSIN * 0.8
      const x = impact[0] + Math.cos(angle) * rayon
      const z = impact[1] + Math.sin(angle) * rayon
      const taille = 0.5 + 0.55 * (0.5 + 0.5 * Math.sin(e * 7.3))
      for (let k = 0; k < 4; k++) {
        const v = e * 4 + k
        centres[v * 3] = x
        centres[v * 3 + 1] = c.hBassin
        centres[v * 3 + 2] = z
        // Le coin est UNITAIRE: le fragment s'en sert comme d'un rayon, et
        // c'est ce qui fait un disque flou la ou la premiere version semait des
        // carres blancs a bord net au pied de la chute.
        coins[v * 2] = k === 0 || k === 3 ? -1 : 1
        coins[v * 2 + 1] = k < 2 ? -1 : 1
        vies[v * 3] = t * 6.28 + e * 1.37
        vies[v * 3 + 1] = 0.55 + 0.5 * (0.5 + 0.5 * Math.cos(e * 3.1))
        vies[v * 3 + 2] = taille
        positions[v * 3] = x
        positions[v * 3 + 1] = c.hBassin + MONTEE_EMBRUNS * 0.5
        positions[v * 3 + 2] = z
        normales[v * 3 + 1] = 1
      }
      const b = e * 4
      index.push(b, b + 1, b + 3, b + 1, b + 2, b + 3)
    }

    const geo = new THREE.BufferGeometry()
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    geo.setAttribute('normal', new THREE.BufferAttribute(normales, 3))
    geo.setAttribute('aCentre', new THREE.BufferAttribute(centres, 3))
    geo.setAttribute('aCoin', new THREE.BufferAttribute(coins, 2))
    geo.setAttribute('aVie', new THREE.BufferAttribute(vies, 3))
    geo.setIndex(index)
    geo.boundingSphere = new THREE.Sphere(
      new THREE.Vector3(impact[0], c.hBassin + MONTEE_EMBRUNS * 0.5, impact[1]),
      RAYON_BASSIN + MONTEE_EMBRUNS,
    )

    const mat = new THREE.MeshBasicMaterial({
      color: BLANC_ECUME,
      transparent: true,
      opacity: 0.32,
      depthWrite: false,
      side: THREE.DoubleSide,
    })
    this.installerNuanceurEmbruns(mat)
    this.ajouter(new THREE.Mesh(geo, mat), 'chute-embruns', 5)
    return index.length / 3
  }

  private ajouter(maille: THREE.Mesh, nom: string, ordre: number): void {
    maille.name = nom
    maille.renderOrder = ordre
    maille.castShadow = false
    maille.receiveShadow = false
    maille.matrixAutoUpdate = false
    maille.updateMatrix()
    this.scene.add(maille)
    this.objets.push(maille)
    const mat = maille.material
    if (Array.isArray(mat)) this.materiaux.push(...mat)
    else this.materiaux.push(mat)
  }

  // -------------------------------------------------------------- nuanceurs

  /**
   * Le rideau: des filets verticaux qui descendent, une eau vitreuse en haut,
   * blanche et dechiree en bas.
   *
   * Le motif ne descend pas d'un bloc: sa vitesse depend du voile (les trois se
   * decalent donc en permanence) et sa phase de la colonne (les filets ne
   * tombent pas en cadence). C'est ce qui empeche de lire une texture qui
   * defile — le defaut classique d'une chute d'eau animee.
   */
  private installerNuanceurLame(materiau: THREE.Material): void {
    materiau.customProgramCacheKey = (): string => 'chute-lame'
    materiau.onBeforeCompile = (shader) => {
      shader.uniforms['uTemps'] = this.uTemps
      shader.vertexShader = shader.vertexShader
        .replace(
          '#include <common>',
          `#include <common>
           attribute vec2 aLame;
           attribute float aPhase;
           attribute float aVitesse;
           varying vec2 vLame;
           varying float vPhase;
           varying float vVitesse;`,
        )
        .replace(
          '#include <begin_vertex>',
          `#include <begin_vertex>
           vLame = aLame;
           vPhase = aPhase;
           vVitesse = aVitesse;`,
        )

      shader.fragmentShader = shader.fragmentShader
        .replace(
          '#include <common>',
          `#include <common>
           uniform float uTemps;
           varying vec2 vLame;
           varying float vPhase;
           varying float vVitesse;`,
        )
        .replace(
          '#include <color_fragment>',
          `#include <color_fragment>
           float bas = vLame.y;
           // Filets verticaux: des cannelures fixes dans la lame, deux echelles
           // pour qu'aucune periode ne se lise.
           float filets = 0.62 + 0.38 * sin(vLame.x * 21.0 + vPhase)
                        + 0.22 * sin(vLame.x * 47.0 - vPhase * 1.7);
           // Paquets d'eau qui DESCENDENT. La descente s'accelere avec la
           // chute, comme l'eau elle-meme: un defilement a vitesse constante
           // trahit tout de suite le truc.
           float course = bas * bas * 3.4 + bas * 1.1;
           float paquet = fract(course * 1.9 - uTemps * vVitesse * 1.35 + vPhase * 0.31);
           float crete = smoothstep(0.45, 0.98, paquet);
           // Le rideau se dechire en bas: des trous mobiles dans la lame.
           float dechirure = smoothstep(0.55, 1.0, bas)
                           * smoothstep(0.35, 0.0, fract(paquet * 2.7 + vLame.x * 3.1));
           float bord = smoothstep(1.0, 0.62, abs(vLame.x));
           diffuseColor.rgb *= 0.62 + 0.52 * filets + 0.38 * crete;
           diffuseColor.a *= bord * (0.28 + 0.72 * smoothstep(0.0, 0.5, bas))
                           * (0.55 + 0.7 * crete) * (1.0 - 0.8 * dechirure);`,
        )
        // La lame est a double face: three.js retourne la normale des faces
        // arriere, le soleil passait donc derriere le rideau et un voile sur
        // deux virait au gris. On reprend celle du sommet, deja redressee vers
        // le haut au montage.
        .replace(
          '#include <normal_fragment_begin>',
          `#include <normal_fragment_begin>
           normal = normalize(vNormal);`,
        )
    }
    materiau.needsUpdate = true
  }

  /** Ruisseau, bourrelet et ecume: un motif qui court le long du courant. */
  private installerNuanceurNappes(materiau: THREE.Material): void {
    materiau.customProgramCacheKey = (): string => 'chute-nappes'
    materiau.onBeforeCompile = (shader) => {
      shader.uniforms['uTemps'] = this.uTemps
      shader.vertexShader = shader.vertexShader
        .replace(
          '#include <common>',
          `#include <common>
           attribute vec3 aCourant;
           varying vec3 vCourant;
           varying vec3 vMonde;`,
        )
        .replace(
          '#include <begin_vertex>',
          `#include <begin_vertex>
           vCourant = aCourant;
           vMonde = position;`,
        )
      shader.fragmentShader = shader.fragmentShader
        .replace(
          '#include <common>',
          `#include <common>
           uniform float uTemps;
           varying vec3 vCourant;
           varying vec3 vMonde;`,
        )
        .replace(
          '#include <color_fragment>',
          `#include <color_fragment>
           // Le motif court dans le sens du courant.
           //
           // DEUX corrections par rapport a la premiere version, qui donnait au
           // ruisseau l'air d'un passage pieton. D'abord l'amplitude: elle
           // faisait varier l'opacite du simple au double. Ensuite, et surtout,
           // les rides etaient fonction de la SEULE distance parcourue, donc
           // rigoureusement perpendiculaires au courant et parfaitement
           // rectilignes d'une rive a l'autre. On mele la coordonnee EN TRAVERS
           // du courant a chaque onde, avec un poids different pour chacune: les
           // cretes deviennent obliques, et aucune ne l'est comme sa voisine.
           float course = vCourant.x;
           float vitesse = vCourant.y;
           float travers = vCourant.z;
           float onde = 0.44 * sin(course * 1.9 + travers * 0.7 - uTemps * vitesse * 2.7)
                      + 0.33 * sin(course * 4.1 - travers * 1.9 - uTemps * vitesse * 3.9)
                      + 0.23 * sin(course * 8.3 + travers * 4.3 - uTemps * vitesse * 5.3);
           diffuseColor.rgb *= 0.95 + 0.1 * onde;
           diffuseColor.a *= 0.93 + 0.1 * onde;`,
        )
        .replace(
          '#include <normal_fragment_begin>',
          `#include <normal_fragment_begin>
           normal = normalize((viewMatrix * vec4(0.0, 1.0, 0.0, 0.0)).xyz);`,
        )
    }
    materiau.needsUpdate = true
  }

  /**
   * Les embruns: montee, dilatation et disparition, entierement dans le
   * nuanceur de sommets, et facettes tournees vers la camera.
   */
  private installerNuanceurEmbruns(materiau: THREE.Material): void {
    materiau.customProgramCacheKey = (): string => 'chute-embruns'
    materiau.onBeforeCompile = (shader) => {
      shader.uniforms['uTemps'] = this.uTemps
      shader.uniforms['uNuit'] = this.uNuit
      shader.vertexShader = shader.vertexShader
        .replace(
          '#include <common>',
          `#include <common>
           uniform float uTemps;
           attribute vec3 aCentre;
           attribute vec2 aCoin;
           attribute vec3 aVie;
           varying float vVie;
           varying vec2 vCoin;`,
        )
        .replace(
          '#include <project_vertex>',
          `// Age dans [0, 1[: chaque gouttelette a sa propre duree de vie, sinon
           // toute la nuee respire d'un seul souffle.
           float age = fract(uTemps * 0.34 * aVie.y + aVie.x);
           vVie = age;
           vCoin = aCoin;
           vec4 mvPosition = vec4(aCentre, 1.0);
           // Elle monte en ralentissant, comme une bouffee d'air humide.
           mvPosition.y += ${MONTEE_EMBRUNS.toFixed(1)} * (1.0 - pow(1.0 - age, 2.2));
           mvPosition = modelViewMatrix * mvPosition;
           // Facette tournee vers la camera: le decalage se fait dans le plan
           // de la vue, ou qu'on ait pivote le monde. Elle grossit en montant.
           mvPosition.xy += aCoin * aVie.z * (0.7 + 1.5 * age);
           gl_Position = projectionMatrix * mvPosition;`,
        )
      shader.fragmentShader = shader.fragmentShader
        .replace(
          '#include <common>',
          `#include <common>
           uniform float uNuit;
           varying float vVie;
           varying vec2 vCoin;`,
        )
        .replace(
          '#include <color_fragment>',
          `#include <color_fragment>
           // Disque flou. Sans lui la facette se voit comme ce qu'elle est: un
           // carre blanc. C'est le defaut qui sautait aux yeux sur les premieres
           // images, un tas de rectangles empiles au pied de la chute.
           float bord = smoothstep(1.0, 0.15, length(vCoin));
           // Bouffee: elle nait, s'epanouit, disparait. Le carre de la sinusoide
           // fait une apparition plus douce qu'un simple triangle.
           float bouffee = sin(3.14159 * clamp(vVie, 0.0, 1.0));
           diffuseColor.a *= bord * bouffee * bouffee * (1.0 - 0.4 * uNuit);`,
        )
    }
    materiau.needsUpdate = true
  }

  // --------------------------------------------------------------------- maj

  maj(tempsSec: number): void {
    this.uTemps.value = tempsSec
  }

  /** 0 = plein jour, 1 = pleine nuit. */
  couleurEau(nuit: number): void {
    const n = clamp(nuit, 0, 1)
    this.uNuit.value = n
    const emission = lerp(EMISSION_JOUR, EMISSION_NUIT, n)
    if (this.matLame) this.matLame.emissiveIntensity = emission
    if (this.matNappes) this.matNappes.emissiveIntensity = emission * PART_EMISSION_NAPPES
  }

  dispose(): void {
    for (const o of this.objets) {
      this.scene.remove(o)
      o.geometry.dispose()
    }
    this.objets.length = 0
    for (const m of this.materiaux) m.dispose()
    this.materiaux.length = 0
    this.matLame = null
    this.matNappes = null
  }
}
